<template>
  <div ref="coursePlanSectionPrerequisite" class="course-plan-section-prerequisite">
    <div ref="fieldGroupRef" class="field-group">
      <label-button v-if="showLabelButton" :disabled="disableLabelButton" @click="handleEdit">
        <template #icon>
          <svg-icon name="AddPrerequisite24" kind="action" />
        </template>
        <template #label>
          {{ t("course_plan_add_prerequisite_label") }}
        </template>
      </label-button>
      <template v-else-if="showPrerequisite">
        <span class="icon">
          <svg-icon :name="lockName" kind="indicative" />
        </span>
        <span class="label" @click.stop.prevent="handleOpen">
          <label-component
            class="resource-prerequisite"
            :text="prerequisiteLabel"
            kind="complete"
          />
        </span>
        <span class="action">
          <cv-button
            v-if="showEditIcon"
            kind="ghost"
            size="small"
            class="icon-button edit"
            @click.stop.prevent="handleEdit"
          >
            <cv-tooltip alignment="center" direction="bottom" :tip="t('button_edit')">
              <svg-icon name="Pencil24" kind="action" />
            </cv-tooltip>
          </cv-button>
        </span>
      </template>
      <span v-if="showLearningAssistant" class="learning-assistant">
        <cv-tooltip
          :tip="t('course_plan_learning_assistant_enabled_label')"
          alignment="center"
          direction="bottom"
        >
          <svg-icon name="LearningAssistant24" kind="indicative" />
        </cv-tooltip>
      </span>
    </div>
    <prerequisite-edit-content-popup
      v-if="isEditPrerequisiteVisible"
      :current-section-id="section.id"
      :prerequisites-drop-down-data="prerequisitesDropDownData"
      :lesson-id="lessonId"
      :game-data="gameData"
      :is-section-prerequisite="true"
      :existing-prerequisites="sectionPrerequisite"
      :existing-operators="existingOperators"
      :reference-element="referenceContainer"
      placement="bottom-start"
      @event="handleEvent"
    />
    <prerequisite-view-content-popup
      v-if="isViewPrerequisiteVisible"
      :current-section-id="section.id"
      :is-section-prerequisite="true"
      :existing-prerequisites="sectionPrerequisite"
      :existing-operators="existingOperators"
      :reference-element="referenceContainer"
      placement="bottom-start"
      :all-other-response-branch-id-list="otherResponseBranchIdListFromExistingPrerequisite"
      :game-awards="gameAwardsFromExistingPrerequisite"
      :is-game-enabled="isGameEnabled"
      @event="handleEvent"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import { EVENT } from "@plugins/course-lesson-plan/course.constant";
import CoursePlanPrerequisiteMixin from "@plugins/course-lesson-plan/mixin/course.plan.prerequisite.mixin";
import PrerequisiteEditContentPopup from "./PrerequisiteEditContentPopup.vue";
import coursePlanStore from "./CoursePlanStore";
import PrerequisiteViewContentPopup from "@plugins/course-lesson-plan/components/PrerequisiteViewContentPopup.vue";
import { GAME_CHOICE_TYPE } from "@modules/editor/service/gamification.constant";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant";
import { RULE_CONDITION_TYPE } from "@modules/editor/service/editor.constant";
import { isDefined, isEmpty } from "@utils/var.check";
import { useVm } from "@utils/vm.ts";
import { INTEGRATOR_APP_FEATURE_CATEGORY } from "@modules/app/service/app.constant.ts";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";

// Mixins
defineOptions({
  mixins: [CoursePlanPrerequisiteMixin],
});

// Props
interface Props {
  section: Record<string, unknown>;
  sectionPrerequisite?: unknown[];
  sectionList: unknown[];
  sequentialSectionCompletionReq?: boolean;
  gameData?: Record<string, unknown>;
  editable?: boolean;
  prerequisitesDropDownData?: Record<string, unknown>;
  lessonId?: number | null;
  existingOperators?: unknown[];
  isLearningAssistantEnabled?: boolean;
}

const {
  section,
  sectionPrerequisite = [],
  sectionList,
  sequentialSectionCompletionReq = false,
  gameData = null,
  editable = false,
  prerequisitesDropDownData = {},
  lessonId = null,
  existingOperators = [],
  isLearningAssistantEnabled = false,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  event: [data: unknown];
}>();

// Composables
const { vm } = useVm();
const { t } = useI18n();
const store = useStore();

const fieldGroupRef = useTemplateRef("fieldGroupRef");

// Computed properties
const isLessonGameEnabled = computed(
  () => store.getters[`${NAMESPACE.RESOURCE}/isLessonGameEnabled`],
);

const getIntegratorAppAccessByAppFeatureId = computed(
  () => store.getters[`${NAMESPACE.APP}/getIntegratorAppAccessByAppFeatureId`],
);

const showLearningAssistant = computed(() => {
  if (!section.appFeatureIds || section.appFeatureIds.length === 0) {
    return false;
  }

  if (!isLearningAssistantEnabled) {
    return false;
  }

  return section.appFeatureIds.some((appFeatureId) => {
    const appFeature = getIntegratorAppAccessByAppFeatureId.value(appFeatureId);
    return (
      !isEmpty(appFeature) && appFeature.category === INTEGRATOR_APP_FEATURE_CATEGORY.LA_TEXT_QUERY
    );
  });
});

const lockName = computed(() => {
  if (section.statusInfo) {
    return vm.sectionPrerequisitesAllComplete ? "Unlock24" : "Lock24";
  }
  return "Lock24";
});

const showEditIcon = computed(() => editable && !sequentialSectionCompletionReq);

const showLabelButton = computed(
  () => showEditIcon.value && vm.sectionPrerequisiteList?.length === 0,
);

const disableLabelButton = computed(() => showEditIcon.value && sectionList.length < 2);

const prerequisiteLabel = computed(() => {
  const prerequisiteCount = vm.sectionPrerequisiteList?.length || 0;
  return t("prerequisite_label", { prerequisiteCount });
});

const editPrerequisitesState = computed({
  get: () => coursePlanStore.contentPopupState().editPrerequisites,
  set: (data: { id: unknown; visible: boolean }) =>
    coursePlanStore.setEditPrerequisitesVisible(data.id, data.visible),
});

const viewPrerequisitesState = computed({
  get: () => coursePlanStore.contentPopupState().viewPrerequisites,
  set: (data: { id: unknown; visible: boolean }) =>
    coursePlanStore.setViewPrerequisitesVisible(data.id, data.visible),
});

const isEditPrerequisiteVisible = computed(() => {
  return !isDefined(editPrerequisitesState.value) ||
    !isDefined(editPrerequisitesState.value[section.id])
    ? false
    : coursePlanStore.isEditPrerequisiteVisible(section.id);
});

const isViewPrerequisiteVisible = computed(() => {
  return !isDefined(viewPrerequisitesState.value) ||
    !isDefined(viewPrerequisitesState.value[section.id])
    ? false
    : coursePlanStore.isViewPrerequisiteVisible(section.id);
});

const referenceContainer = computed(() => {
  return fieldGroupRef.value;
});

const gameAwardsFromExistingPrerequisite = computed(() => {
  if (sectionPrerequisite.length === 0) return [];

  return sectionPrerequisite
    .filter(
      (prerequisite) =>
        prerequisite.criterion === RULE_CONDITION_TYPE.GAME_AWARD ||
        prerequisite.criterion === RULE_CONDITION_TYPE.GAME_CHOICE,
    )
    .map((prerequisite) => {
      const gameAward = {
        criterion: prerequisite.criterion,
        parentId: prerequisite.parentId,
        gameAwardType: prerequisite.gameAwardType,
        choiceType: prerequisite.choiceType,
      };

      if (prerequisite.criterion === RULE_CONDITION_TYPE.GAME_AWARD) {
        gameAward.gameAwardTitle = prerequisite.gameAwardTitle;
      }

      if (prerequisite.criterion === RULE_CONDITION_TYPE.GAME_CHOICE) {
        gameAward.id = parseInt(prerequisite.choiceGameAwardId);
        gameAward.choiceGameAwardTitle = prerequisite.choiceGameAwardTitle;
        gameAward.gameChoiceList = [
          {
            id: prerequisite.gameChoice,
            gameAwardId: prerequisite.gameAwardId,
            choices: prerequisite.choices,
            acceptableResponse: prerequisite.acceptableResponse,
          },
        ];
      }
      return gameAward;
    });
});

const otherResponseBranchIdListFromExistingPrerequisite = computed(() => {
  const list = [];
  gameAwardsFromExistingPrerequisite.value.forEach((item) => {
    if (item.choiceType === GAME_CHOICE_TYPE.TEXT_INPUT && item.gameChoiceList) {
      const gameBranch = item.gameChoiceList.find((gameBranch) => !gameBranch.acceptableResponse);

      if (gameBranch) {
        list.push(gameBranch.id);
      }
    }
  });

  return list;
});

const isGameEnabled = computed(() => gameData?.enabled || isLessonGameEnabled.value);

// Methods
const handleEdit = () => {
  handleClose();
  coursePlanStore.resetContentPopupState();
  setEditPrerequisitesVisible(section.id, true);
};

const handleOpen = () => {
  coursePlanStore.resetContentPopupState();
  coursePlanStore.setViewPrerequisitesVisible(section.id, true);
};

const handleClose = () => {
  coursePlanStore.setViewPrerequisitesVisible(section.id, false);
};

const setEditPrerequisitesVisible = (id, visible) => {
  editPrerequisitesState.value = { id, visible };
};

const setViewPrerequisitesVisible = (id, visible) => {
  viewPrerequisitesState.value = { id, visible };
};

const handleEvent = (event) => {
  const type = event.type;
  switch (type) {
    case EVENT.SAVE_RULE_CONDITION_RESOURCE_PREREQUISITE:
      emit("event", event);
      break;
    case EVENT.TOGGLE_RESOURCE_COMPLETION_PREREQUISITE_CONTENT_POPUP:
      setEditPrerequisitesVisible(event.data.id, event.data.visible);
      break;
    case EVENT.TOGGLE_VIEW_PREREQUISITE_CONTENT_POPUP:
      setViewPrerequisitesVisible(event.data.id, event.data.visible);
      break;
    case EVENT.SECTION_COVER_NAVIGATION:
      emit("event", event);
      break;
    case EVENT.ACTIVITY_NAVIGATION:
      emit("event", event);
      break;
    //TODO handle game navigation
  }
};
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.course-plan-section-prerequisite {
  .field-group {
    margin-bottom: 0;
    display: flex;
    align-items: center;
  }

  .icon {
    display: inline-flex;
    flex: 0 0 auto;
    position: relative;
    margin-right: $spacing-03;
  }

  .resource-prerequisite-popup {
    cursor: default;
  }

  .section-prerequisite-popup {
    .bx--link {
      &:hover {
        color: $link-01;
        text-decoration: none;
      }
    }

    .page-title {
      @include carbon--type-style("panel-title-01", true);
      @include line-clamp(2);
      margin-right: $spacing-03;
      color: $link-01;
    }
  }

  .page-label {
    @include carbon--type-style("helper-text-01", true);
    position: absolute;
    top: 0.7rem;
    left: 50%;
    transform: translateX(-50%);
    color: $ui-01;
  }

  .section-list {
    position: relative;
    padding: $spacing-03 0;

    .wrapper {
      display: flex;
      align-items: center;
      padding: $spacing-03;

      .hide-section-activity-number {
        opacity: 0;

        &.editor-mode {
          opacity: 0.5;
        }
      }
    }

    + .section-list {
      border-top: 1px solid $ui-04;
    }
  }

  .subtitle {
    @include carbon--type-style("panel-title-01", true);
    @include line-clamp(2);
    margin-right: $spacing-03;
    color: $text-02;
  }

  .learning-assistant {
    display: flex;
    align-items: center;
    margin-left: $spacing-03;
    padding-left: $spacing-03;
    border-left: 1px solid $ui-04;

    &:only-child {
      margin-left: 0;
      padding-left: 0;
      border-left: none;
    }
  }
}
</style>
